<script lang="ts" setup>
import {Component} from "vue";
import {ICommonButtonEmits, ICommonButtonProps} from "@/types/main";
import CommonInput from "@/components/common/CommonInput.vue";

const props = withDefaults(defineProps<ICommonButtonProps>(), {
  type: "primary",
  position: "horizontal",
  label: "Button",
  loading: false
});

const emit = defineEmits<ICommonButtonEmits>()

const clickButton = () => {
  emit("clicked");
}
</script>

<template>
  <div class="c-co-button">
    <div class="body" :class="[type, isDisabled ? 'disabled' : '', loading ? 'loading' : '']" @click="clickButton">
      <div v-if="prepend && prependIcon" class="prepend__icon">
        <component :is="prependIcon" :size="20"/>
      </div>
      <div v-if="loading" class="body__loading">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100" preserveAspectRatio="xMidYMid" width="20"
             height="20" style="shape-rendering: auto; display: block;">
          <g data-idx="1">
            <g transform="matrix(1,0,0,1,0,0)" data-idx="2">
              <rect fill="#ffffff" height="13" width="7" ry="6.5" rx="3.5" y="17.5" x="46.5" data-idx="3"
                    opacity="0.225001">

              </rect>
            </g>
            <g transform="matrix(0.7071067811865476,0.7071067811865476,-0.7071067811865476,0.7071067811865476,50,-20.710678118654755)"
               data-idx="5">
              <rect fill="#ffffff" height="13" width="7" ry="6.5" rx="3.5" y="17.5" x="46.5" data-idx="6"
                    opacity="0.350001">

              </rect>
            </g>
            <g transform="matrix(6.123233995736766e-17,1,-1,6.123233995736766e-17,100,0)" data-idx="8">
              <rect fill="#ffffff" height="13" width="7" ry="6.5" rx="3.5" y="17.5" x="46.5" data-idx="9"
                    opacity="0.475001">

              </rect>
            </g>
            <g transform="matrix(-0.7071067811865475,0.7071067811865476,-0.7071067811865476,-0.7071067811865475,120.71067811865476,49.99999999999999)"
               data-idx="11">
              <rect fill="#ffffff" height="13" width="7" ry="6.5" rx="3.5" y="17.5" x="46.5" data-idx="12"
                    opacity="0.600001">

              </rect>
            </g>
            <g transform="matrix(-1,1.2246467991473532e-16,-1.2246467991473532e-16,-1,100,100)" data-idx="14">
              <rect fill="#ffffff" height="13" width="7" ry="6.5" rx="3.5" y="17.5" x="46.5" data-idx="15"
                    opacity="0.725001">

              </rect>
            </g>
            <g transform="matrix(-0.7071067811865477,-0.7071067811865475,0.7071067811865475,-0.7071067811865477,50.000000000000014,120.71067811865476)"
               data-idx="17">
              <rect fill="#ffffff" height="13" width="7" ry="6.5" rx="3.5" y="17.5" x="46.5" data-idx="18"
                    opacity="0.850001">

              </rect>
            </g>
            <g transform="matrix(-1.8369701987210297e-16,-1,1,-1.8369701987210297e-16,7.105427357601002e-15,100)"
               data-idx="20">
              <rect fill="#ffffff" height="13" width="7" ry="6.5" rx="3.5" y="17.5" x="46.5" data-idx="21"
                    opacity="0.975001">

              </rect>
            </g>
            <g transform="matrix(0.7071067811865474,-0.7071067811865477,0.7071067811865477,0.7071067811865474,-20.710678118654755,50.000000000000014)"
               data-idx="23">
              <rect fill="#ffffff" height="13" width="7" ry="6.5" rx="3.5" y="17.5" x="46.5" data-idx="24"
                    opacity="0.100001">

              </rect>
            </g>
            <g data-idx="26"></g>
          </g>
        </svg>
      </div>
      <div v-if="label" class="body__text">
        {{ label }}
      </div>
      <slot v-else></slot>
      <div v-if="append && appendIcon" class="append__icon">
        <component :is="appendIcon" :size="20"/>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.c-co-button {
  .body {
    padding: 12px 24px;
    background: var(--color-primary);
    border-radius: 14px;
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 8px;
    color: var(--color-white);
    cursor: pointer;

    .prepend__icon {
      width: 20px;
      height: 20px;
      background: transparent;
    }

    &__text {
      font-size: 14px;
      line-height: 20px;
      letter-spacing: 0;
      font-weight: 700;
      background: transparent;
    }

    &__loading {
      svg {
        animation: spin 1s linear infinite;
        transform-origin: center;
        will-change: transform;
      }
    }

    .append__icon {
      width: 20px;
      height: 20px;
      background: transparent;
    }

    &.disabled {
      background: var(--color-secondary-label);
      cursor: not-allowed;
    }

    &.loading {
      cursor: not-allowed;
    }

    &.outline {
      background: var(--color-white);
      border: 1px solid var(--color-label);
      padding: 11px 23px;
      color: var(--color-label) !important;

      &.disabled {
        color: var(--color-secondary-label) !important;
        border-color: var(--color-secondary-label);
      }
    }

    &.secondary {
      background: var(--color-bg-secondary);
      color: var(--color-label) !important;

      &.disabled {
        color: var(--color-secondary-label) !important;
      }
    }

    &.error {
      background: var(--color-white);
      border: 1px solid var(--color-red);
      padding: 11px 23px;
      color: var(--color-red) !important;

      &.disabled {
        color: #E01E3780 !important;
        border-color: #E01E3780;
      }
    }

    &.link {
      color: var(--color-label);
      background: var(--color-white);

      &.disabled {
        color: var(--color-secondary-label) !important;
      }
    }
  }
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}
</style>
